import { getRecordsForCompositeTargetCollection } from '../../reducers/standard_reducer_utils';
import {
	nullSafeGet,
	nullSafeGetOrElse,
	valueCompareWithoutType,
} from '../../utils/DataAccessUtils';
import { LIMIT_ONE_PAGINATION, ROLE_TYPES, WO_STEP_NAMES } from '../../utils/DataConstants';
import { getAssetFilterForWOCreation } from '../../utils/WorkOrderUtils';
import {
	getActiveAssetWarrantySupplierId,
	getActiveBuildingWarrantySupplierId,
} from '../../utils/WarrantyUtils';

export interface PT_STEP_PROPS {
	fetchAssets: any;
	goNext: any;
	setState: any;
	companyConfig: any;
	currentUser: any;
	getCurrentState: any;
	problemTypes: any;
	TC_NAME: string;
	fetchProblemType: any;
	fetchSupplierFacilitiesInPrivateNetwork: any;
	fetchSupplierFacility: any;
	fetchWorkOrderAttributes: any;
	fetchWorkOrderPriority: any;
	setNTE: any;
	changeId: any;
	form: any;
}

export const PT_LEVELS = {
	TOP_LEVEL: 'problemTypeTopLevel',
	NEXT_LEVEL: 'problemTypeNextLevel',
	NEXT_BRANCH: 'problemTypeNextBranch',
};

interface PT_FIELDS {
	currentStep: string;
	nextStep: string;
	prevLevelId: string;
	currentLevelId: string;
	nextLevelAvailable: string;
}

export const getProblemTypeStepStateVariables = (fieldName: string): PT_FIELDS => {
	switch (fieldName) {
		case PT_LEVELS.NEXT_LEVEL:
			return {
				currentStep: WO_STEP_NAMES.PROBLEM_TYPE_NEXT_LEVEL,
				nextStep: WO_STEP_NAMES.PROBLEM_TYPE_NEXT_BRANCH,
				currentLevelId: 'problemTypeLevel2Id',
				prevLevelId: 'problemTypeLevel1Id',
				nextLevelAvailable: 'problemTypeLevel3Available',
			};
		case PT_LEVELS.NEXT_BRANCH:
			return {
				currentStep: WO_STEP_NAMES.PROBLEM_TYPE_NEXT_LEVEL,
				nextStep: WO_STEP_NAMES.PROBLEM_TYPE_NEXT_BRANCH,
				currentLevelId: 'problemTypeLevel3Id',
				prevLevelId: 'problemTypeLevel2Id',
				nextLevelAvailable: 'problemTypeLevel4Available',
			};
		default:
			return {
				currentStep: WO_STEP_NAMES.PROBLEM_TYPE_TOP_LEVEL,
				nextStep: WO_STEP_NAMES.PROBLEM_TYPE_NEXT_LEVEL,
				currentLevelId: 'problemTypeLevel1Id',
				prevLevelId: '',
				nextLevelAvailable: 'problemTypeLevel2Available',
			};
	}
};

const handleNumPicturesRequiredForProblemType = (args: PT_STEP_PROPS) => (problemType) => {
	const { setState } = args;
	setState({
		numPicturesRequired: nullSafeGetOrElse('serviceRequestNumPicturesRequired', problemType, 0),
	});
};

const checkAssetAndProceed =
	(args: PT_STEP_PROPS) =>
	(currentStep, ignoreTroubleshooting = false) => {
		const { fetchAssets, companyConfig, currentUser, getCurrentState, setState, goNext } = args;

		const state = getCurrentState();
		fetchAssets(
			getAssetFilterForWOCreation(ROLE_TYPES.BUYER)(companyConfig, currentUser, state),
			null,
			LIMIT_ONE_PAGINATION
		).then((assets) => {
			const assetsAvailable = !!(assets && assets.length > 0);

			goNext(
				[],
				assetsAvailable ? WO_STEP_NAMES.ASSET : WO_STEP_NAMES.TITLE,
				currentStep,
				ignoreTroubleshooting
			);
			setState({ assetsAvailable });
		});
	};

const getProblemTypeObject = (args: PT_STEP_PROPS) => (problemTypeId) => {
	const { problemTypes, TC_NAME, fetchProblemType } = args;
	const records = getRecordsForCompositeTargetCollection(problemTypes, TC_NAME);

	const pt = records.find(({ id }) => valueCompareWithoutType(id, problemTypeId));
	if (pt) {
		return Promise.resolve(pt);
	}

	return fetchProblemType(problemTypeId);
};

const setOtherValuesForChildProblemType = (args: PT_STEP_PROPS) => (problemType) => {
	const {
		getCurrentState,
		fetchSupplierFacilitiesInPrivateNetwork,
		fetchSupplierFacility,
		fetchWorkOrderAttributes,
		fetchWorkOrderPriority,
		setState,
		setNTE,
		form,
	} = args;
	const state = getCurrentState();
	const { currencyExchangeRateBuyerToLocation } = state;

	const warrantySupplierId =
		getActiveBuildingWarrantySupplierId(nullSafeGet('location', state), problemType.id) ||
		getActiveAssetWarrantySupplierId(nullSafeGet('asset', state));

	if (warrantySupplierId) {
		setState({ supplier: { id: warrantySupplierId } });
		if (form && form.setFieldsValue) {
			form.setFieldsValue({ supplierFacilityId: warrantySupplierId });
		}
		if (fetchSupplierFacility) {
			fetchSupplierFacility(warrantySupplierId)
				.then((warrantySupplier) => {
					setState({ supplier: warrantySupplier });
				})
				.catch(() => {});
		}
	}

	const supplierFacilitiesPromise = fetchSupplierFacilitiesInPrivateNetwork(
		{
			problemTypeId: problemType.id,
			locationId: nullSafeGet('location.id', state),
		},
		null,
		LIMIT_ONE_PAGINATION
	);
	const workOrderAttributePromise = fetchWorkOrderAttributes(
		{ problemTypeId: problemType.id },
		null,
		LIMIT_ONE_PAGINATION
	);

	Promise.all([supplierFacilitiesPromise, workOrderAttributePromise])
		.then((values) => {
			handleNumPicturesRequiredForProblemType(problemType);

			const supplierFacility = values[0];
			if (!warrantySupplierId) {
				if (supplierFacility.length > 0) {
					setState({ supplier: supplierFacility[0] });
				} else {
					setState({ supplier: null });
				}
			}

			const workOrderAttributes = values[1];
			setState({ workOrderAttributes });
			if (nullSafeGetOrElse('length', workOrderAttributes, 0) > 0) {
				if (workOrderAttributes[0].NTEAmount !== undefined) {
					setNTE(workOrderAttributes[0].NTEAmount * currencyExchangeRateBuyerToLocation);
				}
				if (workOrderAttributes[0].workOrderPriorityId) {
					setState({ workOrderPriorityId: workOrderAttributes[0].workOrderPriorityId }, () => {
						fetchWorkOrderPriority(workOrderAttributes[0].workOrderPriorityId).then(
							(woPriority) => {
								setState({ woPriority });
							}
						);
					});
				}
				if (workOrderAttributes[0].workOrderTitle && !form.getFieldValue('title')) {
					form.setFieldsValue({ title: workOrderAttributes[0].workOrderTitle });
				}
			}
		})
		.catch(console.log);
};

const handleProblemTypeChange: any = (args: PT_STEP_PROPS) => (problemTypeId) => {
	const { setState } = args;
	return new Promise((resolve, reject) => {
		if (problemTypeId) {
			getProblemTypeObject(args)(problemTypeId).then((problemType) => {
				setState({ problemType }, () => {
					if (!problemType.hasChildren) {
						setOtherValuesForChildProblemType(args)(problemType);
					}
					resolve(problemType);
				});
			});
		} else {
			reject('Problem type id not found!');
		}
	});
};

export const handleProblemTypeChangeOrClick =
	(args: PT_STEP_PROPS) =>
	(fieldName, problemTypeId, ignoreTroubleshooting = false) => {
		const { changeId, setState, goNext } = args;
		const stateVariables = getProblemTypeStepStateVariables(fieldName);
		changeId(stateVariables.currentLevelId, problemTypeId);
		handleProblemTypeChange(args)(problemTypeId).then((problemType) => {
			if (problemType.hasChildren) {
				goNext([], stateVariables.nextStep, stateVariables.currentStep, false, false, {
					[stateVariables.currentLevelId]: problemTypeId,
					[stateVariables.nextLevelAvailable]: true,
					problemTypePriorLevelId: problemTypeId,
					problemTypePriorLevel: problemType.name,
					spendCategoryId: problemType.spendCategoryId,
				});
			} else {
				setState(
					{
						[stateVariables.nextLevelAvailable]: false,
						spendCategoryId: problemType.spendCategoryId,
						problemTypePriorLevelId: undefined,
						problemTypePriorLevel: undefined,
					},
					() => {
						checkAssetAndProceed(args)(stateVariables.currentStep, ignoreTroubleshooting);
					}
				);
			}
		});
	};
